import SearchPanel from '@components/searchPanel'
import { Grid } from '@mui/material'
import * as common from '@common/common'
import { GlobalContext } from '@providers/globalStore'
import { InitialSearchPanelState, SearchPanelContext } from '@providers/SearchPanelProvider'
import SearchPanelReducer, { SearchPanelState } from '@reducers/searchPanelReducer'
import moment from 'moment'
import { Fragment, useContext, useReducer, useRef, useState } from 'react'
import { NavigateFunction } from 'react-router-dom'
import useTableView from '@hooks/useTableView'
import { useTranslation } from 'react-i18next'
import FormStatusLabel from '@components/FormStatusLabel'
import GetValueWithKey from '@utils/getValueWithKey'

import { FormTwoListModel } from '@services/model/form/form.TWO.model'
import FormTWOService from '@services/formService/form.TWO.service'
import { NavigateTo } from '@utils/navigate'
import { RightsCategory } from '@services/model/form/form.model'
import { KeyValPair } from '@models/common'
import { BaseFilterOptions } from '@services/model/base.model'

export default function AllRecord(props: { showMyRecord?: boolean }) {
  const refMounted = useRef(false)
  const { state: globalState } = useContext(GlobalContext)
  const [state, dispatch] = useReducer<React.Reducer<SearchPanelState, any>>(
    SearchPanelReducer,
    InitialSearchPanelState,
  )

  const { t } = useTranslation()
  const [extraListInfo, setExtraListInfo] = useState<BaseFilterOptions>({
    submittedByList: [],
    approvedByList: [],
    formIdList: [],
  })

  const getExtraListInfo = async () => {
    let extraInfo = await FormTWOService.GetTwoFilterOption(props.showMyRecord)
    setExtraListInfo(extraInfo)
    refMounted.current = true
  }

  const [TableView, reload] = useTableView<FormTwoListModel>({
    headers: [
      {
        key: 'formStatusShortName',
        desc: t('General Status', { ns: common.i18nFormNS }),
        renderCell: (record: FormTwoListModel) => (
          <Fragment>
            <FormStatusLabel
              label={record.formStatusShortName}
              color={record.formStatusColor || ''}
            />
          </Fragment>
        ),
      },
      {
        key: 'formStatus',
        desc: t('Submission Status', { ns: common.i18nFormNS }),
        renderCell: (record: FormTwoListModel) => <Fragment>{record.formStatus}</Fragment>,
      },
      {
        key: 'formId',
        desc: t('Form ID', { ns: common.i18nFormNS }),
        renderCell: (record: FormTwoListModel) => <Fragment>{record.reportNo}</Fragment>,
      },
      {
        key: 'descriptionOfWorks',
        desc: t('Description of Works', { ns: common.i18nFormNS }),
        renderCell: (record: FormTwoListModel) => <Fragment>{record.descriptionOfWorks}</Fragment>,
      },
      {
        key: 'submittedBy',
        desc: t('Submitted By', { ns: common.i18nFormNS }),
        renderCell: (record: FormTwoListModel) => (
          <Fragment>{GetValueWithKey(record.submittedBy, globalState.userMetaList ?? [])}</Fragment>
        ),
      },
      {
        key: 'approvedBy',
        desc: t('Approved By', { ns: common.i18nFormNS }),
        renderCell: (record: FormTwoListModel) => (
          <Fragment>{GetValueWithKey(record.approvedBy, globalState.userMetaList ?? [])}</Fragment>
        ),
      },
      {
        key: 'updatedAt',
        desc: t('Last Update', { ns: common.i18nFormNS }),
        renderCell: (record: FormTwoListModel) => (
          <Fragment>
            {record.updatedAt ? moment(record.updatedAt).format(moment.defaultFormat) : ''}
          </Fragment>
        ),
      },
    ],
    onRowClick: function (navigate: NavigateFunction, record: FormTwoListModel) {
      NavigateTo(navigate, '/temporary-works-order/:id', { id: record.formId })
    },
    mountedRef: refMounted,
    reloadCallback: async (pagination: object, sorting: object | undefined, cancelToken: any) => {
      if (refMounted.current) {
        sessionStorage.setItem('TWO', JSON.stringify(state.filterResult))
        let resp = await FormTWOService.GetTwoList(
          {
            ...state.filterResult,
            sorting: sorting,
            pagination: pagination,
          },
          cancelToken,
          props.showMyRecord || false,
        )
        return resp
      }
    },
  })

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)

  const [inactiveReason, setInactiveReason] = useState<string>('')
  const [targetInactiveBookingId, setTargetInactiveBookingId] = useState<number>(0)

  return (
    <Grid component="main" container padding={1}>
      <SearchPanelContext.Provider value={{ state, dispatch }}>
        <SearchPanel
          dispatch={dispatch}
          addUrl="/booking/new"
          onSearch={reload}
          onInitReload={reload}
          onToggleFilterDrawer={getExtraListInfo}
          mountedRef={refMounted}
          excludeStatus={true}
          criteria={[
            {
              key: 'formStatus',
              desc: 'Submission Status',
              type: 'NumberSelectionFilter',
              multiple: true,
              valueList:
                globalState.formStatusList
                  ?.filter((x) => x.actionCode.includes(RightsCategory.FORM_TWO))
                  .map((x) => {
                    return {
                      key: x.id,
                      value: x.actionName,
                    }
                  }) || [],
              defaultValue:
                sessionStorage?.TWO !== 'undefined' && sessionStorage?.TWO !== undefined
                  ? JSON.parse(sessionStorage?.TWO)?.formStatus?.value
                  : [],
            },
            {
              key: 'formId',
              desc: 'Form ID',
              type: 'StringSelectionFilter',
              multiple: true,
              valueList: extraListInfo.formIdList,
              defaultValue:
                sessionStorage?.TWO !== 'undefined' && sessionStorage?.TWO !== undefined
                  ? JSON.parse(sessionStorage?.TWO)?.formId?.value
                  : [],
            },
            {
              key: 'descriptionOfWorks',
              desc: 'Description Of Works',
              type: 'StringFilter',
              defaultValue:
                sessionStorage?.TWO !== 'undefined' && sessionStorage?.TWO !== undefined
                  ? JSON.parse(sessionStorage?.TWO)?.descriptionOfWorks?.value
                  : '',
            },
            {
              key: 'submittedBy',
              desc: 'Submitted By',
              type: 'StringSelectionFilter',
              multiple: true,
              valueList: globalState.userMetaList?.filter((x) =>
                extraListInfo.submittedByList?.includes(x.key),
              ),
              defaultValue:
                sessionStorage?.TWO !== 'undefined' && sessionStorage?.TWO !== undefined
                  ? JSON.parse(sessionStorage?.TWO)?.submittedBy?.value
                  : [],
            },
            {
              key: 'approvedBy',
              desc: 'Approved By',
              type: 'StringSelectionFilter',
              multiple: true,
              valueList: globalState.userMetaList?.filter((x) =>
                extraListInfo.approvedByList?.includes(x.key),
              ),
              defaultValue:
                sessionStorage?.TWO !== 'undefined' && sessionStorage?.TWO !== undefined
                  ? JSON.parse(sessionStorage?.TWO)?.approvedBy?.value
                  : [],
            },
            {
              key: 'updatedAt',
              desc: 'Last Update',
              type: 'DateFilter',
              defaultOperator:
                sessionStorage?.TWO !== 'undefined' && sessionStorage?.TWO !== undefined
                  ? JSON.parse(sessionStorage?.TWO)?.inspectionDate?.operator
                  : 'in',
              defaultFrom:
                sessionStorage?.TWO !== 'undefined' && sessionStorage?.TWO !== undefined
                  ? JSON.parse(sessionStorage?.TWO)?.inspectionDate?.value?.min
                  : '',
              defaultTo:
                sessionStorage?.TWO !== 'undefined' && sessionStorage?.TWO !== undefined
                  ? JSON.parse(sessionStorage?.TWO)?.inspectionDate?.value?.max
                  : '',
            },
          ]}
        />
        <TableView />
      </SearchPanelContext.Provider>
    </Grid>
  )
}
